import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import Button from './components/ui/button';
import SwedishTimeCounter from './SwedishTimeCounter';
import InteractiveMap from './components/InteractiveMap';
import AboutPage from './pages/AboutPage';

const SPEED_OPTIONS = [
  { label: "1 mån/sek", value: 1 },
  { label: "6 mån/sek", value: 6 },
  { label: "1 år/sek", value: 12 }, // 1 år = 12 månader
  { label: "5 år/sek", value: 60 }, // 5 år = 60 månader
  { label: "10 år/sek", value: 120 }, // 10 år = 120 månader
];

function Navigation() {
  const location = useLocation();
  const isAboutPage = location.pathname === '/om';

  return (
    <nav className="flex items-center space-x-4">
      {!isAboutPage && (
        <Link 
          to="/om" 
          className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
        >
          Om
        </Link>
      )}
      {isAboutPage && (
        <Link 
          to="/" 
          className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded-md text-sm font-medium"
        >
          Tillbaka till kartan
        </Link>
      )}
      <a 
        href="https://github.com/yourusername/tillhoertsverige" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
      >
        GitHub
      </a>
    </nav>
  );
}

function MainApp() {
  const [currentDate, setCurrentDate] = useState(new Date("1523-01-01T02:00:00"));
  const [isPlaying, setIsPlaying] = useState(true); // Startar direkt om "true"
  const [speed, setSpeed] = useState(12); // Defaultvärdet 12 månader per sekund (1 år / sek)
  const [selectedRegionId, setSelectedRegionId] = useState(null); // Ny state för vald region
  const intervalRef = useRef(null);

  // Funktion för att hantera förändringar i inputfältet
  const handleDateChange = (e) => {
  const selectedDate = new Date(e.target.value);
  
  // Hämta nuvarande tid i svensk tid
  const now = new Date();
  const stockholmNow = new Date(now.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }));

  // Om det valda datumet är i framtiden, sätt det till nuvarande datum och tid
  if (selectedDate > stockholmNow) {
    setCurrentDate(stockholmNow);
  } else {
    setCurrentDate(selectedDate);
  }
};

  // Hantering av tickande funktion (Play-paus funktion)
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentDate((prev) => {
          const newDate = new Date(prev);
          newDate.setMonth(newDate.getMonth() + speed); // Här läggs hastigheten till i månader

          // Om det nya datumet är större än dagens datum, stanna på dagens datum
          const now = new Date();
          const stockholmNow = new Date(now.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }));

          if (newDate > stockholmNow) {
            return stockholmNow;
          }
          return newDate;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, speed]);

  // Funktion för att hantera region-val från kartan eller räknarna
  const handleRegionSelect = (regionId) => {
    console.log('App: handleRegionSelect anropad med:', regionId);
    setSelectedRegionId(regionId);
  };

  return (
    <main className="min-h-screen bg-gray-100 p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Hur länge har landsdelar varit svenska?</h1>
        <Navigation />
      </div>

      {/* Karta + kontroller */}
      <section className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Kartsektion - tar upp 3 kolumner på större skärmar */}
        <div className="lg:col-span-3">
          <div className="bg-white shadow-lg rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Sveriges territoriella förändringar</h2>
            <div className="w-full h-[800px] bg-gray-50 rounded-xl overflow-hidden">
              <InteractiveMap 
                currentDate={currentDate} 
                onRegionSelect={handleRegionSelect}
                selectedRegionId={selectedRegionId}
              />
            </div>
          </div>
        </div>

        {/* Sidopanel med kontroller - tar upp 1 kolumn */}
        <aside className="lg:col-span-1">
          <div className="bg-white shadow-lg rounded-2xl p-6 space-y-6">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800">Tidskontroller</h2>
              
              {/* Play/Paus knappar */}
              <div className="flex flex-wrap gap-2">
                {!isPlaying ? (
                  <Button onClick={() => setIsPlaying(true)}>Play</Button>
                ) : (
                  <Button variant="outline" onClick={() => setIsPlaying(false)}>
                    Paus
                  </Button>
                )}
                <Button variant="outline" onClick={() => setCurrentDate(new Date(currentDate.setFullYear(currentDate.getFullYear() + 10)))}>
                  +10 år
                </Button>
                <Button variant="outline" onClick={() => setCurrentDate(new Date(currentDate.setFullYear(currentDate.getFullYear() - 10)))}>
                  -10 år
                </Button>
              </div>
              
              {/* Hastighetskontroll */}
              <div>
                <label className="text-sm font-medium text-gray-700">Hastighet:</label>
                <select
                  value={speed}
                  onChange={(e) => setSpeed(Number(e.target.value))}
                  className="w-full mt-2 rounded-lg border border-gray-300 px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {SPEED_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Datuminput */}
              <div>
                <label className="text-sm font-medium text-gray-700">Välj datum:</label>
                <input
                  type="datetime-local"
                  value={new Intl.DateTimeFormat('sv-SE', {
                    timeZone: 'Europe/Stockholm',
                    hour12: false,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                  .format(currentDate)  // Formaterar korrekt svensk tid
                  .replace(' ', 'T') // Byt mellanslag mot T
                  .slice(0, 16)} // Hämta endast datum och tid (yyyy-MM-ddTHH:mm)
                  onChange={handleDateChange}
                  className="w-full mt-2 rounded-lg border border-gray-300 px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  max={new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }).slice(0, 16)} // Sätt maxvärde till dagens datum i svensk tid
                />
              </div>
            </div>
          </div>
        </aside>
      </section>

      {/* Räknare - nu med tydlig separation från kartan */}
      <section className="bg-white shadow-lg rounded-2xl p-6">
        <SwedishTimeCounter 
          currentDate={currentDate} 
          selectedRegionId={selectedRegionId}
          onRegionSelect={handleRegionSelect}
        />
      </section>

      {/* Footer */}
      <footer className="text-xs text-center mt-8 mb-4 px-4 text-gray-500">
        Changes done on original map by <a href="//commons.wikimedia.org/wiki/User:CrazyPhunk" title="User:CrazyPhunk" className="underline hover:text-blue-500">CrazyPhunk</a> - self-made, based on 
        <a href="//commons.wikimedia.org/wiki/File:Northern-Europe-map.png" title="File:Northern-Europe-map.png" className="underline hover:text-blue-500"> Image:Northern-Europe-map.png</a>, 
        <a href="http://creativecommons.org/licenses/by-sa/3.0/" title="Creative Commons Attribution-Share Alike 3.0" className="underline hover:text-blue-500">CC BY-SA 3.0</a>, 
        <a href="https://commons.wikimedia.org/w/index.php?curid=2524630" className="underline hover:text-blue-500">Link</a>
      </footer>
    </main>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/om" element={<AboutPage />} />
      <Route path="/" element={<MainApp />} />
    </Routes>
  );
}
